import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Briefcase,
  BadgeCheck,
  BarChart3,
  Music2,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OFFER_PUBLISH_HEAT } from "@/lib/offers.functions";
import { MARKETPLACE_OG_IMAGE, MARKETPLACE_OG_IMAGE_ALT, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";
import { useMarketplaceAccess } from "@/lib/marketplace/flags";
import { MarketplaceHome } from "@/components/marketplace/marketplace-home";
import { MarketplacePreviewRibbon } from "@/components/marketplace/gate";

export const Route = createFileRoute("/marketplace/")({
  head: () =>
    buildPageMetadata({
      path: "/marketplace",
      title: "Influencer & Creator Marketplace — Coming Soon | BeHot.Now",
      description:
        "Find creators, influencers and UGC talent on the BeHot.Now marketplace: verified audience data, public briefs, clipping and sound campaigns, protected payments. Coming soon.",
      keywords: [
        "influencer marketplace",
        "creator marketplace",
        "hire creators",
        "brand creator collabs",
        "UGC marketplace",
        "find influencers",
      ],
      ogTitle: "Creator marketplace — coming soon",
      ogDescription:
        "Creators, brands, agencies, curators and labels in one marketplace. Launching soon on BeHot.Now.",
      image: MARKETPLACE_OG_IMAGE,
      imageAlt: MARKETPLACE_OG_IMAGE_ALT,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Marketplace", path: "/marketplace" },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "BeHot.Now creator marketplace",
        serviceType: "Influencer, UGC and creator collaboration marketplace",
        description:
          "Marketplace connecting creators with brands, agencies, curators and labels for UGC, clipping, sound and sponsorship campaigns.",
        url: `${SITE_URL}/marketplace`,
      },
    }),
  component: Marketplace,
});

const FEATURE_ICONS = [BadgeCheck, Briefcase, Music2, ShieldCheck, BarChart3, Sparkles];
const STEP_COUNT = 4;

function Marketplace() {
  const { t } = useT();
  const { allowed, canPreview, isPublic, isLoading } = useMarketplaceAccess();

  if (!isLoading && allowed) {
    return (
      <>
        {canPreview && !isPublic && <MarketplacePreviewRibbon />}
        <MarketplaceHome />
      </>
    );
  }

  const MARKET_FEATURES = FEATURE_ICONS.map((icon, i) => ({
    icon,
    title: t(`marketing.marketplace.feature.${i}.title`),
    body: t(`marketing.marketplace.feature.${i}.body`, { n: OFFER_PUBLISH_HEAT }),
  }));

  const MARKET_STEPS = Array.from({ length: STEP_COUNT }, (_, i) => ({
    title: t(`marketing.marketplace.step.${i}.title`),
    body: t(`marketing.marketplace.step.${i}.body`, { n: OFFER_PUBLISH_HEAT }),
  }));

  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <section className="card-surface p-8 sm:p-10">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="uppercase tracking-widest">
            {t("marketing.marketplace.badge_name")}
          </Badge>
          <Badge variant="outline" className="uppercase tracking-widest">
            {t("marketing.marketplace.badge_soon")}
          </Badge>
        </div>
        <h1 className="mt-4 font-display text-4xl font-bold sm:text-5xl">
          {t("marketing.marketplace.h1_pre")}{" "}
          <span className="text-gradient-hot">{t("marketing.marketplace.h1_grad")}</span>
        </h1>
        <p className="mt-3 max-w-3xl text-muted-foreground">
          {t("marketing.marketplace.subtitle")}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild variant="hot">
            <Link to="/get-hot">{t("marketing.marketplace.cta_board")}</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/earn">{t("marketing.marketplace.cta_earn")}</Link>
          </Button>
          <Button asChild variant="ghost">
            <Link to="/promote">{t("marketing.marketplace.cta_promote")}</Link>
          </Button>
        </div>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-3">
        {MARKET_FEATURES.map((f) => (
          <div key={f.title} className="card-surface p-6">
            <f.icon className="size-6 text-primary" />
            <h2 className="mt-3 font-display text-lg font-bold">{f.title}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{f.body}</p>
          </div>
        ))}
      </section>

      <section className="card-surface mt-8 p-8">
        <Badge variant="outline" className="uppercase tracking-widest">
          {t("marketing.marketplace.two_sides")}
        </Badge>
        <div className="mt-5 grid gap-6 md:grid-cols-2">
          <div>
            <h2 className="font-display text-xl font-bold">{t("marketing.marketplace.creator_heading")}</h2>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li>{t("marketing.marketplace.creator_li1")}</li>
              <li>{t("marketing.marketplace.creator_li2")}</li>
              <li>{t("marketing.marketplace.creator_li3")}</li>
              <li>{t("marketing.marketplace.creator_li4")}</li>
            </ul>
          </div>
          <div>
            <h2 className="font-display text-xl font-bold">{t("marketing.marketplace.pro_heading")}</h2>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li>
                {t("marketing.marketplace.pro_li1")}
              </li>
              <li>{t("marketing.marketplace.pro_li2")}</li>
              <li>
                {t("marketing.marketplace.pro_li3", { n: OFFER_PUBLISH_HEAT })}
              </li>
              <li>{t("marketing.marketplace.pro_li4")}</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="card-surface mt-8 p-8">
        <h2 className="font-display text-2xl font-bold">{t("marketing.marketplace.how_heading")}</h2>
        <ol className="mt-5 grid gap-4 md:grid-cols-4">
          {MARKET_STEPS.map((step, i) => (
            <li key={step.title} className="rounded-xl border border-border p-5">
              <span className="font-display text-3xl font-bold text-gradient-hot">{i + 1}</span>
              <p className="mt-2 font-display font-semibold">{step.title}</p>
              <p className="mt-1 text-sm text-muted-foreground">{step.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="card-surface mt-8 p-8 text-center">
        <Badge variant="outline" className="uppercase tracking-widest">
          {t("marketing.marketplace.badge_soon")}
        </Badge>
        <h2 className="mt-3 font-display text-2xl font-bold">
          {t("marketing.marketplace.build_heading")}
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
          {t("marketing.marketplace.build_body")}
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <Button asChild variant="hot">
            <Link to="/login">{t("marketing.marketplace.cta_create")}</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/how-it-works">{t("marketing.marketplace.cta_how")}</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
